import type { SignInOutput } from "aws-amplify/auth"
import { autoSignIn, confirmSignIn, confirmSignUp } from "aws-amplify/auth"
import { useAuth } from "hooks/use-auth"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MfaChallenge } from "./mfa-challenge"
import { Alert, AlertDescription } from "./ui/alert"
import { Button } from "./ui/button"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  REGEXP_ONLY_DIGITS,
} from "./ui/input-otp"

interface EmailOTPVerificationProps {
  email: string
  onBack: () => void
  isLoading?: boolean
  mode: "signup" | "signin"
}

export function EmailOTPVerification({
  email,
  onBack,
  isLoading: externalLoading = false,
  mode,
}: EmailOTPVerificationProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [mfaStep, setMfaStep] = useState<SignInOutput["nextStep"] | null>(null)
  const { checkAuthState } = useAuth()
  const loading = isLoading || externalLoading
  const otpLength = mode === "signin" ? 8 : 6

  async function handleComplete(otp: string) {
    if (otp.length !== otpLength) return

    setIsLoading(true)
    setError(null)

    try {
      if (mode === "signup") {
        const { nextStep: confirmSignUpNextStep } = await confirmSignUp({
          username: email,
          confirmationCode: otp,
        })

        if (confirmSignUpNextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
          const { nextStep } = await autoSignIn()

          if (nextStep.signInStep === "DONE") {
            checkAuthState()
          } else {
            setMfaStep(nextStep)
          }
        }
      } else {
        const { nextStep } = await confirmSignIn({
          challengeResponse: otp,
        })

        if (nextStep.signInStep === "DONE") {
          checkAuthState()
        } else {
          // Cognito carries the optional second-factor challenge in the same
          // nextStep once the email-OTP first factor succeeds.
          setMfaStep(nextStep)
        }
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : t("components.emailOTPVerification.invalidCode"),
      )
      setValue("") // Clear the OTP input on error
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSubmit() {
    if (value.length === otpLength) {
      await handleComplete(value)
    } else {
      setError(t("components.emailOTPVerification.incompleteCode", { length: otpLength }))
    }
  }

  if (mfaStep) {
    return (
      <MfaChallenge
        initialStep={mfaStep}
        email={email}
        onSignedIn={checkAuthState}
        onBack={() => {
          setMfaStep(null)
          onBack()
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="text-center">
        <h2 className="text-lg font-semibold">{t("components.emailOTPVerification.title")}</h2>
        <p className="text-sm text-muted-foreground mt-2">
          {t("components.emailOTPVerification.instruction")} {email}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="otp-input" className="text-sm font-medium">
            {t("components.emailOTPVerification.codeLabel")}
          </label>
          <div className="flex justify-center">
            <InputOTP
              id="otp-input"
              maxLength={otpLength}
              pattern={REGEXP_ONLY_DIGITS}
              value={value}
              autoFocus
              onChange={(value) => {
                setValue(value)
                setError(null) // Clear error when user starts typing
                if (value.length === otpLength) {
                  handleComplete(value) // Auto-submit when complete
                }
              }}
              disabled={loading}
            >
              <InputOTPGroup>
                {Array.from({ length: Math.ceil(otpLength / 2) }, (_, i) => {
                  // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-length list, index is stable
                  return <InputOTPSlot key={`otp-slot-${i}`} index={i} />
                })}
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                {Array.from({ length: Math.floor(otpLength / 2) }, (_, i) => {
                  const actualIndex = i + Math.ceil(otpLength / 2)
                  return <InputOTPSlot key={`otp-slot-${actualIndex}`} index={actualIndex} />
                })}
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          className="w-full"
          disabled={loading || value.length !== otpLength}
        >
          {loading
            ? t("components.emailOTPVerification.verifyingText")
            : t("components.emailOTPVerification.verifyButton")}
        </Button>

        <Button
          variant="outline"
          onClick={onBack}
          className="w-full"
          disabled={loading}
          type="button"
        >
          {mode === "signup"
            ? t("components.emailOTPVerification.backToSignUp")
            : t("components.emailOTPVerification.backToSignIn")}
        </Button>
      </div>
    </div>
  )
}
